import { default as Modal } from 'antd/es/modal';
import { default as Skeleton } from 'antd/es/skeleton';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import { getDisbursementPayoutReportsUuid } from 'api';

import { useOrganisationId } from 'hooks/context';
import { useAsyncMemo } from 'hooks/useAsyncMemo';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { DownloadThinIcon } from '@atoms/Icon/DownloadThinIcon';

import { DownloadPayoutRequestsCard } from './DownloadPayoutRequestsCard';

import {
  DOWNLOAD_CSV_BUTTON_TEST_ID,
  EXPORTED_PAYOUTS_MODAL_TEST_ID
} from '../../constants';

type Props = {
  downloadReport: (uuid: string) => void;
};

export const DownloadPayoutRequestsDialog = ({ downloadReport }: Props) => {
  const organisationId = useOrganisationId<number>();
  const [searchParams] = useSearchParams();
  const openNotification = useOpenNotification();
  const uuid = searchParams.get('download_payouts') || '';
  const [open, setOpen] = useState<boolean>(uuid !== '');
  const reportInfo = useAsyncMemo(async () => {
    if (!uuid) return undefined;
    try {
      const retval = await getDisbursementPayoutReportsUuid(
        organisationId,
        uuid
      );
      return retval.data;
    } catch {
      openNotification({
        kind: 'error',
        message: 'Could not find Payout Report with that uuid',
        key: 'fetch-payout-requests-info'
      });
      setOpen(false);
    }
  }, [organisationId, uuid]);

  const handleDownload = (uuid: string) => {
    downloadReport(uuid);
    setOpen(false);
  };

  return (
    <Modal
      className="top-1/4"
      data-testid={EXPORTED_PAYOUTS_MODAL_TEST_ID}
      title={
        <div className="mb-6 flex flex-row">
          <DownloadThinIcon />
          <div className="pl-4">Download payout requests</div>
        </div>
      }
      open={open}
      onCancel={() => setOpen(false)}
      footer={
        <PrimaryButton
          className="mt-4 w-full"
          data-testid={DOWNLOAD_CSV_BUTTON_TEST_ID}
          disabled={!Boolean(reportInfo)}
          onClick={() => {
            reportInfo && handleDownload(reportInfo.uuid);
          }}
        >
          Download CSV report
        </PrimaryButton>
      }
      width={336}
    >
      {reportInfo ? (
        <DownloadPayoutRequestsCard
          createdAfter={reportInfo.created_after}
          createdBefore={reportInfo.created_before}
          virtualAccountNumber={
            reportInfo.virtual_account_number || 'All accounts'
          }
          stateCategories={reportInfo.state_categories}
        />
      ) : (
        <Skeleton />
      )}
    </Modal>
  );
};
